import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  badgesFromNotifications,
  fetchUnseenChatProcessNotifications,
  markChatProcessNotificationsSeen,
  observeChatProcessNotifications,
  type ChatProcessNotification,
  type ConversationProcessBadge,
} from "../lib/chat-process-notifications";

function conversationIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/chat\/([^/]+)/);
  return match?.[1] ?? null;
}

function upsertNotification(
  current: ChatProcessNotification[],
  next: ChatProcessNotification
): ChatProcessNotification[] {
  const without = current.filter((item) => item.id !== next.id);
  return [...without, next];
}

export function useChatProcessNotifications(): {
  badgeByConversationId: Map<string, ConversationProcessBadge>;
} {
  const { pathname } = useLocation();
  const activeConversationId = conversationIdFromPath(pathname);
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  const [notifications, setNotifications] = useState<ChatProcessNotification[]>([]);
  const seenInFlightRef = useRef<Set<string>>(new Set());

  const markSeen = useCallback(async (conversationId: string) => {
    if (!conversationId || seenInFlightRef.current.has(conversationId)) return;
    seenInFlightRef.current.add(conversationId);
    setNotifications((prev) => prev.filter((item) => item.conversationId !== conversationId));
    try {
      await markChatProcessNotificationsSeen(conversationId);
    } catch (error) {
      console.error("[chat-process-notifications] mark seen failed", error);
    } finally {
      seenInFlightRef.current.delete(conversationId);
    }
  }, []);

  const applyNotifications = useCallback(
    (next: ChatProcessNotification[]) => {
      const activeId = activeConversationIdRef.current;
      if (activeId && next.some((item) => item.conversationId === activeId)) {
        void markSeen(activeId);
        setNotifications(next.filter((item) => item.conversationId !== activeId));
        return;
      }
      setNotifications(next);
    },
    [markSeen]
  );

  const isChatRoute = pathname.startsWith("/chat");

  useEffect(() => {
    if (!isChatRoute) {
      setNotifications([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let reconnectTimer: number | undefined;

    async function loadSnapshot() {
      try {
        const snapshot = await fetchUnseenChatProcessNotifications();
        if (!cancelled) applyNotifications(snapshot);
      } catch (error) {
        if (!cancelled) {
          console.error("[chat-process-notifications] snapshot failed", error);
        }
      }
    }

    async function connect() {
      await loadSnapshot();
      if (cancelled) return;
      try {
        await observeChatProcessNotifications({
          signal: controller.signal,
          onSnapshot: (snapshot) => {
            if (!cancelled) applyNotifications(snapshot);
          },
          onUpdated: (notification) => {
            if (cancelled) return;
            const activeId = activeConversationIdRef.current;
            if (activeId && notification.conversationId === activeId) {
              void markSeen(activeId);
              return;
            }
            setNotifications((prev) => upsertNotification(prev, notification));
          },
        });
      } catch (error) {
        if (controller.signal.aborted || cancelled) return;
        console.error("[chat-process-notifications] stream failed", error);
      }
      // Reconnect after drop while still on /chat.
      if (!cancelled && !controller.signal.aborted) {
        reconnectTimer = window.setTimeout(() => {
          if (!cancelled) void connect();
        }, 2000);
      }
    }

    void connect();
    return () => {
      cancelled = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      controller.abort();
    };
  }, [applyNotifications, isChatRoute, markSeen]);

  useEffect(() => {
    if (!activeConversationId) return;
    if (!notifications.some((item) => item.conversationId === activeConversationId)) return;
    void markSeen(activeConversationId);
  }, [activeConversationId, markSeen, notifications]);

  const badgeByConversationId = useMemo(() => {
    const badges = badgesFromNotifications(notifications);
    if (activeConversationId) badges.delete(activeConversationId);
    return badges;
  }, [activeConversationId, notifications]);

  return { badgeByConversationId };
}
